// 報告頁主報告、/discussion 與 /history 的請求範圍（報告改版工單 14；站5 修正 H1、第 8 項；/history＝工單 25 PR-B）。
// 純函式、不 import 任何模組（tests/report-shell.test.mjs 直接測）；由 stores/modules/report.js 使用。
//
// 背景：報告頁殼（pages/Report/index.vue）先發主報告與 /discussion，再載入渲染器 chunk；舊渲染器掛載時照舊呼叫
// 同一批 store action。兩件事要同時成立：
//   - 去重：同一 type＋token＋lang 進行中的請求共用同一個 promise（dedupe）。
//   - 一次頁殼載入內不自動重打：某 type＋token＋lang 在本次頁殼載入內已結束（成功或失敗）就記為 settled，
//     渲染器的自動流程（掛載、watcher）遇到 settled 一律不重抓——否則主報告失敗（store 的 reportData 仍為 null）
//     或回 200 但沒有 record 時，舊渲染器掛載會再打一次：後端「未完成就 sleep 最多 6 秒」的等待從約 6 秒變約 12 秒、
//     限流額度吃兩倍。使用者按的明確重試（呼叫端帶 retry）不受 settled 限制。
//   - settled 只在「同一次頁殼載入」內有效：頁殼每次 load()（掛載、SPA 內換報告、重新打開報告）呼叫 beginShellLoad 清空，
//     所以失敗不會被永久記住，下次打開會重試。token／lang 變動（store 的 CLEAR_DATA）時也清空。
// 不放進 Vuex state：純流程控制，不需要響應式，也不該進 devtools 快照。

const keyOf = (token, lang) => `${token}\u0000${lang}`

export function createReportRequestScope () {
  const inflight = new Map()
  const settled = new Map() // type → keyOf(token, lang)

  return {
    /** 頁殼開始一次新的載入：本範圍的 settled 紀錄全部作廢（進行中的請求不受影響，仍可被去重共用）。 */
    beginShellLoad () {
      settled.clear()
    },
    /** token 或 lang 變動（store CLEAR_DATA）：舊的 settled 紀錄不再適用。 */
    resetSettled () {
      settled.clear()
    },
    /** 本次頁殼載入內，type＋token＋lang 是否已結束過（成功或失敗）。 */
    isSettled (type, token, lang) {
      return settled.get(type) === keyOf(token, lang)
    },
    markSettled (type, token, lang) {
      settled.set(type, keyOf(token, lang))
    },
    /**
     * 同一 type＋token＋lang 進行中時共用同一個 promise；結束後移除。run 是 async 函式，**同步**呼叫
     * （與改版前相同：SET_LOADING 等 commit 在 dispatch 當下就發生，畫面不會先閃一次空狀態）。
     */
    dedupe (type, token, lang, run) {
      const key = `${type}\u0000${keyOf(token, lang)}`
      if (inflight.has(key)) return inflight.get(key)
      const promise = run().finally(() => inflight.delete(key))
      inflight.set(key, promise)
      return promise
    },
    /** 同一 type＋token＋lang 是否有進行中的請求。 */
    isInflight (type, token, lang) {
      return inflight.has(`${type}\u0000${keyOf(token, lang)}`)
    },
    /**
     * 強制重抓（報告改版工單 25 PR-B 站5 D6）：沒有進行中的請求＝照常 dedupe（發一次）；已有進行中的請求（例：與主報告同時發出、
     * 在報告不能顯示期間的 /history）＝共用它，結束後 stillNeeded() 仍為真才再發一次（只再一次，不迴圈）。
     * 為什麼：dedupe 讓強制重抓只拿到舊的在途結果（多半是「錨點尚未完成」），報告恢復了資料卻要等下次頁殼載入。
     */
    refreshAfterInflight (type, token, lang, run, stillNeeded) {
      const joined = this.isInflight(type, token, lang)
      const promise = this.dedupe(type, token, lang, run)
      if (!joined) return promise
      return promise.then(() => (stillNeeded() ? this.dedupe(type, token, lang, run) : undefined))
    },
  }
}
